import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * What the screening agent decided, and on what evidence.
 *
 * Every rule is shown with its score, not only the ones that fired: seeing that a rule came back at
 * 20 is what lets an administrator tell an agent that is too shy from one that is badly configured,
 * and set the threshold on something other than a hunch.
 */
const HiddenVerdict = ({ output }) => {
  const { t } = useTranslation();
  const details = output?.details || {};
  const rules = details.rules || [];
  const hidden = details.decision === 'hide';

  return (
    <div className="space-y-3">
      <div className={`rounded-xl p-3 ${hidden
        ? 'bg-amber-50 dark:bg-amber-950/40'
        : 'bg-green-50 dark:bg-green-950/40'}`}
      >
        <p className={`text-sm font-semibold ${hidden
          ? 'text-amber-900 dark:text-amber-200'
          : 'text-green-900 dark:text-green-200'}`}
        >
          {hidden ? t('Hidden from clients') : t('Left visible to clients')}
        </p>
        {details.reason && (
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{details.reason}</p>
        )}
      </div>

      {output?.summary && <p className="text-sm text-gray-700 dark:text-gray-300">{output.summary}</p>}

      <ul className="space-y-1.5">
        {rules.map((rule) => {
          const fired = rule.confidence >= (details.min_confidence ?? 0);
          return (
            <li key={rule.key} className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-800">
              <div className="flex items-baseline justify-between gap-3">
                <span className={`text-sm ${fired
                  ? 'font-semibold text-amber-900 dark:text-amber-200'
                  : 'text-gray-700 dark:text-gray-300'}`}
                >
                  {t(rule.label)}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                  {rule.confidence}
                </span>
              </div>
              {rule.evidence && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{rule.evidence}</p>
              )}
            </li>
          );
        })}
      </ul>

      {details.min_confidence !== undefined && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('Hiding threshold: {{threshold}}', { threshold: details.min_confidence })}
        </p>
      )}
    </div>
  );
};

export default HiddenVerdict;
